/**
 * Wizard navigation buttons
 *
 * @author Fabio Dal Santo <fabio@gestionaleauto.com>
 */

/**
 * Main
 */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';

/**
 * Redux
 */
import { useSelector, useDispatch } from 'react-redux';
import {
  resetSuccess,
  sendDataToServer, setSubStep,
  wizardState, setStep, validateForm,
} from '../../redux/wizardSlice';
import { leadState } from '../../redux/leadSlice';
import { steps, tradeSubSteps } from '../../helpers/const';
/**
 * Icons
 */
import { Arrow } from '../../components/SVGIcons';

const useStyles = makeStyles((theme) => ({
  navigationButtons: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    rowGap: 16,
    [theme.breakpoints.up('md')]: {
      justifyContent: 'space-between',
      flexDirection: 'row',
      rowGap: 0,
      columnGap: 16,
    },
  },
  navigationButton: {
    width: '100%',
    minWidth: 'unset',
    position: 'relative',
    '&.first-step': {
      width: '50%',
      marginLeft: 'auto',
    },
  },
  arrow: {
    position: 'absolute',
    top: 19,
    width: 20,
    height: 'auto',
  },
  arrowBack: {
    transform: 'rotate(90deg)',
    left: 13,
  },
  arrowNext: {
    transform: 'rotate(-90deg)',
    right: 13,
  },
}));

/**
 * @param firstStep {number}: first step (difference between quotation and ecommerce)
 * @param stepPrevSize {number}: size of backstep status change
 * @param stepNextSize {number}: size of step status change
 * @param enablePrev {boolean}: states if prev step can be reached
 * @param enableNext {boolean}: states if next step is available and can be reached
 * @param needSubmit {boolean}: states if data submit is needed
 * @param disabledNext {boolean}: disabled next button
 * @param hidePrev {boolean}: Hide prev button
 *
 * @returns {JSX.Element}
 */
function Navigation({
  firstStep, stepPrevSize, stepNextSize, enablePrev,
  enableNext, needSubmit, disabledNext,
  hidePrev, callBackNext, callBackPrev, callBackSuccess,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    loading, success, error, errorMessage, step, subStep,
  } = useSelector(wizardState);
  const { quoteSteps: subStepsState } = useSelector(leadState);

  useEffect(() => {
    if (success) {
      if (callBackSuccess) { callBackSuccess(); return; }
      if (subStep <= tradeSubSteps.mileage && step === steps.trade) {
        dispatch(setSubStep(subStep + 1));
      } else {
        dispatch(setStep(stepNextSize));
        dispatch(resetSuccess());
      }
    }
  }, [success]);

  return (
    <>
      <div className={classes.navigationButtons}>
        { !hidePrev && (
          <button
            type="button"
            className={`ga-button-ecommerce-back ${classes.navigationButton}`}
            onClick={
              () => {
                if (!callBackPrev) {
                  if (step === steps.trade && subStep > 0) {
                    dispatch(setSubStep(subStep - 1));
                  } else {
                    dispatch(setStep(stepPrevSize));
                  }
                } else {
                  callBackPrev();
                }
              }
            }
            disabled={!enablePrev}
          >
            <Arrow className={`${classes.arrow} ${classes.arrowBack}`} />
            {t('back')}
          </button>
        )}
        <button
          type="button"
          disabled={disabledNext}
          className={`ga-button-default ga-button-ecommerce-next ${classes.navigationButton} ${loading ? 'loading' : ''} ${step === firstStep ? 'first-step' : ''}`}
          onClick={
            () => {
              if (!enableNext) {
                dispatch(validateForm(true));
                return;
              }
              if (needSubmit) {
                dispatch(sendDataToServer());
                return;
              }
              if (!callBackNext) {
                if (step === steps.trade
                  && subStep < 3
                  && subStepsState[subStep].isValid) {
                  dispatch(setSubStep(subStep + 1));
                  return;
                }
                if (step !== steps.trade
                  || (step === steps.trade
                    && subStep === 3)) {
                  dispatch(setStep(stepNextSize));
                }
              } else {
                callBackNext();
              }
            }
          }
        >
          <Arrow className={`${classes.arrow} ${classes.arrowNext}`} />
          {t('Next')}
        </button>
      </div>
      { error && errorMessage !== '' && (
        <div className="ga-form-output-error">
          {t(errorMessage)}
        </div>
      )}
    </>
  );
}

Navigation.propTypes = {
  firstStep: PropTypes.number,
  stepPrevSize: PropTypes.number,
  stepNextSize: PropTypes.number,
  enablePrev: PropTypes.bool,
  enableNext: PropTypes.bool,
  needSubmit: PropTypes.bool,
  disabledNext: PropTypes.bool,
  hidePrev: PropTypes.bool,
  callBackNext: PropTypes.func,
  callBackPrev: PropTypes.func,
  callBackSuccess: PropTypes.func,
};

Navigation.defaultProps = {
  firstStep: 0,
  stepPrevSize: 1,
  stepNextSize: 1,
  enablePrev: true,
  enableNext: true,
  needSubmit: false,
  disabledNext: false,
  hidePrev: false,
  callBackNext: undefined,
  callBackPrev: undefined,
  callBackSuccess: undefined,
};

export default React.memo(Navigation);
